import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getStringParam, getIntParam, mergeSearch } from '../../../../utils/urlParams';
import { resourceDisplayName } from '../../../../utils/resourceName';
import usePageTitle from '../../../../hooks/usePageTitle';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useBreadcrumbs } from '../../../../contexts/BreadcrumbContext';
import {
  setSelectedMetric as setSelectedMetricAction,
  fetchMonthlyStats,
  fetchHorizonOverview,
  fetchActiveUsers,
  fetchMonthOverMonthStats,
  fetchGateClosureOverview,
  fetchGateHourDetail,
  setSelectedReportHorizon,
  setSelectedGateHour,
  clearGateHourDetail,
  openDayDetail,
  selectMonthlyStatsData,
  selectMonthlyStatsLoading,
  selectMonthlyStatsError,
  selectHorizonOverviewData,
  selectHorizonOverviewLoading,
  selectHorizonOverviewError,
  selectSelectedReportHorizon,
  selectActiveUsers,
  selectActiveUsersLoading,
  selectMonthOverMonthData,
  selectMonthOverMonthLoading,
  selectMonthOverMonthError,
  selectGateClosureData,
  selectGateClosureLoading,
  selectGateHourDetailData,
  selectGateHourDetailLoading,
  selectSelectedGateHour,
} from '../../../../slices/reportsSlice';
import {
  fetchResourceParticipants,
  selectParticipants,
  selectParticipantsLoading,
  selectParticipantsResourceId,
} from '../../../../slices/userSlice';
import {
  fetchAvailableHorizons,
  selectAvailableHorizons,
} from '../../../../slices/marketSlice';
import {
  selectAuthIsForecaster,
  selectAuthIsMarketOwner,
  selectAuthIsAdmin,
  selectAuthIsMarketOwnerReadOnly
} from '../../../../slices/authSlice';
import PermissionChecker from '../../../../utils/permissionChecker';
import { selectBlurSensitive, selectPreferredModel } from '../../../../slices/preferencesSlice';
import { ENSEMBLE_MODELS } from '../../../../constants/ensembleModels';
import useSelectedResource from '../../../../hooks/useSelectedResource';
import RankingsReport from './RankingsReport';
import ErrorsReport from './ErrorsReport';
import PaymentsReport from './PaymentsReport';
import MobileAnalyticsSummary from './MobileAnalyticsSummary';
import InsightsReport from './InsightsReport';
import TrendsReport from './trends/TrendsReport';
import DayDetailDrawer from './dayDetail/DayDetailDrawer';
import AnalyticsFiltersBar from '../../../../components/AnalyticsFiltersBar';
import TopProgressBar from '../../../../components/TopProgressBar';
import DNComplianceEmptyState from '../../../../components/analytics/DNComplianceEmptyState';
import ExcludedDaysNotice from '../../../../components/analytics/ExcludedDaysNotice';
import {ExcludedDaysProvider} from '../../../../contexts/ExcludedDaysContext';
import { monthOptions } from '../../../../helpers/datetimeHelpers';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, BoxPlotController, BoxAndWiskers, zoomPlugin);

// Align Chart.js text with the app's body font stack. Chart.js's built-in
// default is "Helvetica Neue", which on macOS renders distinctly from the
// SF-Pro-resolved system stack used everywhere else, making chart axis
// numbers look like a different typeface than the surrounding UI.
// Chart.js font family is configured globally in src/utils/chartDefaults.js.

// Valid tab ids for the selectedReport state and the ?tab= URL param.
const REPORT_TAB_IDS = ['trends', 'rankings', 'errors', 'payments'];

// The internal 'trends' id is labeled "Overview" in the UI, so the shareable
// URL uses the visible name. Mapping happens only at the URL boundary; the
// internal id stays 'trends' (it is referenced throughout this file).
// 'trends' is still accepted on read for old links.
const TAB_TO_URL = { trends: 'overview' };
const URL_TO_TAB = { overview: 'trends' };
const URL_TAB_VALUES = [...REPORT_TAB_IDS.map((id) => TAB_TO_URL[id] ?? id), 'trends'];

// Refs outside component to persist across unmount/remount
// These survive when navigating away and returning to the page
let savedUserRef = null;
let savedYearRef = null;
let savedMonthRef = null;
let savedForecastTypeRef = null;

// Drop saved year/month if the current calendar month has moved past them.
// Without this, returning to Analytics after a month rollover would restore
// the previous month instead of defaulting to the current one.
const isSavedMonthStale = () => {
  if (savedYearRef == null || savedMonthRef == null) return false;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  return (
    savedYearRef < currentYear ||
    (savedYearRef === currentYear && savedMonthRef < currentMonth)
  );
};

// Below lg the standalone forecaster view swaps the tabbed UI for
// MobileAnalyticsSummary. Evaluated once at module scope: it seeds the
// monthly-stats fetch gate only, so a mid-session resize just means the
// summary waits for the next tab-driven fetch (acceptable).
const IS_PHONE_VIEWPORT =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 1023px)').matches;

export default function Reports() {
  usePageTitle('Analytics');
  useBreadcrumbs([{ label: 'Analytics' }], 'Platform-wide analytics and forecaster rankings');

  const dispatch = useDispatch();
  const selectedResource = useSelectedResource();
  const participants = useSelector(selectParticipants);
  const participantsLoading = useSelector(selectParticipantsLoading);
  const participantsResourceId = useSelector(selectParticipantsResourceId);
  const monthlyStatsData = useSelector(selectMonthlyStatsData);
  const monthlyStatsLoading = useSelector(selectMonthlyStatsLoading);
  const monthlyStatsError = useSelector(selectMonthlyStatsError);
  const horizonOverviewData = useSelector(selectHorizonOverviewData);
  const horizonOverviewLoading = useSelector(selectHorizonOverviewLoading);
  const horizonOverviewError = useSelector(selectHorizonOverviewError);
  const selectedReportHorizon = useSelector(selectSelectedReportHorizon);
  // Canonical horizon set for the resource (from active challenge templates),
  // independent of the selected forecaster. Drives the full filter list.
  const canonicalHorizons = useSelector(selectAvailableHorizons);
  const activeUsers = useSelector(selectActiveUsers);
  const activeUsersLoading = useSelector(selectActiveUsersLoading);
  const monthOverMonthData = useSelector(selectMonthOverMonthData);
  const monthOverMonthLoading = useSelector(selectMonthOverMonthLoading);
  const monthOverMonthError = useSelector(selectMonthOverMonthError);
  const gateClosureData = useSelector(selectGateClosureData);
  const gateClosureLoading = useSelector(selectGateClosureLoading);
  const gateHourDetailData = useSelector(selectGateHourDetailData);
  const gateHourDetailLoading = useSelector(selectGateHourDetailLoading);
  const selectedGateHour = useSelector(selectSelectedGateHour);

  // Logged-in user from Redux (only used for display purposes)
  const authState = useSelector(state => state.auth);
  const user = authState?.user;
  // const userLabel = user ? ((user.first_name || '') + (user.last_name ? ' ' + user.last_name : '') || user.username || user.email) : null;
  const isForecaster = useSelector(selectAuthIsForecaster);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const blurSensitive = useSelector(selectBlurSensitive);
  // Trends ensemble strategy: defaults to the user's preferred model (settings)
  // and can be switched in the filter bar. Owner/admin only.
  const preferredModel = useSelector(selectPreferredModel);
  const [trendsEnsembleModel, setTrendsEnsembleModel] = useState(preferredModel || 'weighted_avg');
  // Overview "Oracle" overlay: daily best-forecaster error envelope on the
  // errors-over-time chart. Off by default; toggled in the filter bar.
  const [showOracle, setShowOracle] = useState(false);
  // Overview tab: rank the ensemble + contracted forecasters into the field
  // (default), or show the official competitor-only ranking when off.
  const [showReference, setShowReference] = useState(true);
  // Forecasters spotlight peers on the Overview via a trace kept separate from
  // `selectedUser`: the leaderboard is anonymised and read-only, whereas the
  // detail tabs force a forecaster to their own data, so the two must not share
  // state (a peer id must never leak into a Rankings/Errors fetch).
  const [forecasterTrace, setForecasterTrace] = useState(null);
  const handleForecasterTrace = useCallback(
    (userId) => setForecasterTrace(userId || null),
    [],
  );
  const permissionChecker = new PermissionChecker({
    isAdmin, isMarketOwner, isMarketOwnerReadOnly, isForecaster});
  // User has permission to view other users' reports if they are market_owner, market_owner_read, or admin
  // If they DON'T satisfy this (e.g., forecaster), they have NO permission to view others
  const hasNoPermission = !permissionChecker.satisfies(['market_owner', 'market_owner_read', 'admin']);

  // "The selection is settled — leave it alone." Set once a user has been
  // auto-picked, restored, or explicitly kept across a resource switch. Always
  // starts false on mount so a selection restored from savedUserRef is
  // re-validated against the current period.
  const hasAutoSelectedRef = React.useRef(false);
  // Seeds the forecaster's Overview spotlight to themselves once; a later
  // deselect must stick, so we never re-seed after the first run.
  const hasSeededForecasterTraceRef = React.useRef(false);

  // Default to current month and year
  const getCurrentMonthYear = () => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1 // JavaScript months are 0-indexed, we need 1-indexed
    };
  };

  // Get previous month and year (used specifically for Payments section)
  const getPreviousMonthYear = () => {
    const now = new Date();
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      year: prevMonthDate.getFullYear(),
      month: prevMonthDate.getMonth() + 1 // JavaScript months are 0-indexed, we need 1-indexed
    };
  };

  const defaultMonthYear = getCurrentMonthYear();
  const previousMonthYear = getPreviousMonthYear();

  // UI state - initialize from saved module variables or defaults
  const [selectedReport, setSelectedReport] = useState('trends');
  const [selectedUser, setSelectedUser] = useState(savedUserRef || '');
  const [forecastType, setForecastType] = useState(savedForecastTypeRef || 'deterministic');

  // Shared state for Rankings and Errors sections.
  // Discard saved month/year if it predates the current calendar month
  // (e.g., user last visited in March, comes back in April).
  const staleSavedMonth = isSavedMonthStale();
  if (staleSavedMonth) {
    savedYearRef = null;
    savedMonthRef = null;
  }
  const [sharedYear, setSharedYear] = useState(savedYearRef || defaultMonthYear.year);
  const [sharedMonth, setSharedMonth] = useState(savedMonthRef || defaultMonthYear.month);

  // Forecasters get the phone summary; privileged views keep the tabbed UI at
  // every size.
  const mobileSummaryActive = hasNoPermission;

  // URL sync
  const location = useLocation();
  const navigate = useNavigate();
  const urlSeeded = React.useRef(false);

  // Separate state for Payments section
  const [paymentsYear, setPaymentsYear] = useState(previousMonthYear.year);
  const [paymentsMonth, setPaymentsMonth] = useState(previousMonthYear.month);

  // Generate year options (current year and past 2 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const years = [];
    for (let i = currentYear; i >= currentYear - 2; i--) {
      years.push(i);
    }
    return years;
  }, [currentYear]);


  // Map forecast type to metric
  const forecastTypeToMetric = {
    deterministic: 'rmse',
    probabilistic: 'mwi'
  };

  // Get the metric based on selected forecast type
  const currentMetric = forecastTypeToMetric[forecastType];

  // Get current metric label
  const currentMetricLabel = forecastType === 'deterministic' ? 'RMSE' : 'MWI';

  // Fetch participants when component mounts or selected resource changes
  // Only fetch if user has permission (admins/market owners need the list for the user selector)
  useEffect(() => {
    if (selectedResource?.id && !hasNoPermission) {
      dispatch(fetchResourceParticipants(selectedResource.id));
    }
  }, [dispatch, selectedResource?.id, hasNoPermission]);

  // Fetch the canonical horizon set for the resource (challenge templates),
  // so the horizon filter shows every configured horizon regardless of which
  // forecaster is selected.
  useEffect(() => {
    if (selectedResource?.id) {
      dispatch(fetchAvailableHorizons({ resourceId: selectedResource.id }));
    }
  }, [dispatch, selectedResource?.id]);

  // Filter participants based on active users
  const filteredParticipants = useMemo(() => {
    // If no active users data, return all participants (fallback)
    if (!activeUsers || activeUsers.length === 0) {
      return participants;
    }

    // Map user_id → activeUsers row so we can attach has_stats. has_stats is
    // false for forecasters who submitted in the period but produced no
    // MarketMonthlyStats (typically Rule 7 D+N noncompliance) — the dropdown
    // tags them so admins can pick them out at a glance.
    const activeMap = new Map(activeUsers.map((u) => [u.user_id, u]));

    return participants
      .filter((p) => activeMap.has(p.user))
      .map((p) => ({
        ...p,
        has_stats: activeMap.get(p.user)?.has_stats ?? true,
      }));
  }, [participants, activeUsers]);

  // Horizons the *selected forecaster* actually has monthly stats on (derived
  // from the per-user overview). Used to grey out the no-data horizons.
  const horizonsWithData = useMemo(() => {
    if (!Array.isArray(horizonOverviewData)) return [];
    return [...horizonOverviewData]
      .sort((a, b) => (a.horizon_offset || 0) - (b.horizon_offset || 0))
      .map((r) => r.horizon_offset);
  }, [horizonOverviewData]);

  // Full horizon set shown in the filter: the canonical resource horizons.
  // Fall back to the forecaster's data horizons until the canonical list lands.
  const availableHorizons = useMemo(() => {
    if (Array.isArray(canonicalHorizons) && canonicalHorizons.length > 0) {
      return [...canonicalHorizons].sort((a, b) => a - b);
    }
    return horizonsWithData;
  }, [canonicalHorizons, horizonsWithData]);

  // The forecaster selection vs. the header resource picker.
  //
  // A resource switch does NOT drop the selection outright: forecasters usually
  // participate in several resources, and wiping the picker on every switch
  // (which is what this used to do) is disorienting. Instead we flag the
  // selection for validation and let the effect below decide once the NEW
  // resource's participant list lands. Until it does, `participants` still holds
  // the previous resource's rows, so there is nothing to check against yet.
  //
  // Mount is deliberately not a switch: the selection restored from savedUserRef
  // (or seeded from ?user=) stays, and the validation below still runs against
  // whatever participant list this resource has.
  const selectionResourceRef = React.useRef(null);
  const pendingSelectionCheckRef = React.useRef(Boolean(savedUserRef));
  useEffect(() => {
    const resourceId = selectedResource?.id;
    if (resourceId == null) return; // resource not resolved yet
    const prev = selectionResourceRef.current;
    selectionResourceRef.current = resourceId;
    if (prev === null || prev === resourceId) return; // mount, or no real change

    setForecasterTrace(null); // the Overview spotlight is per-resource
    hasSeededForecasterTraceRef.current = false; // re-seed self on the new resource
    if (hasNoPermission) return; // forecasters stay pinned to their own data

    if (selectedUser) {
      // Freeze the selection until we know whether it survives the switch, so
      // the auto-pick / restore / swap effects below can't act on the previous
      // resource's (still-cached) participants and activeUsers in the meantime.
      pendingSelectionCheckRef.current = true;
      hasAutoSelectedRef.current = true;
    } else {
      hasAutoSelectedRef.current = false; // let the auto-pick run for the new resource
    }
  }, [selectedResource?.id, selectedUser, hasNoPermission]);

  // Resolve a pending check once the participant list for the current resource
  // has actually landed: keep the forecaster if they participate here, drop
  // them (and re-enable the auto-pick) if they don't.
  useEffect(() => {
    if (!pendingSelectionCheckRef.current || hasNoPermission) return;
    if (participantsLoading || participantsResourceId !== selectedResource?.id) return;
    pendingSelectionCheckRef.current = false;
    if (!selectedUser) return;

    // Ids reach us as numbers from the participant list but as strings from the
    // ?user= param, so compare them as strings.
    const target = String(selectedUser);
    if (participants.some((p) => String(p.user) === target)) return;

    setSelectedUser('');
    savedUserRef = null;
    hasAutoSelectedRef.current = false;
  }, [
    hasNoPermission,
    selectedUser,
    participants,
    participantsLoading,
    participantsResourceId,
    selectedResource?.id,
  ]);

  // URL -> state, once on mount.
  useEffect(() => {
    if (urlSeeded.current) return;
    urlSeeded.current = true;
    const search = location.search;

    const rawTab = getStringParam(search, 'tab', { fallback: null, allowed: URL_TAB_VALUES });
    const tab = rawTab ? (URL_TO_TAB[rawTab] ?? rawTab) : null;
    if (tab) setSelectedReport(tab);

    const track = getStringParam(search, 'track', { fallback: null, allowed: ['deterministic', 'probabilistic'] });
    if (track) setForecastType(track);

    // The forecaster selection is only meaningful for viewers who can pick
    // other users (owners/admins); forecasters are always pinned to their own
    // data, so a ?user= param is ignored for them (and never written back).
    const user = hasNoPermission ? null : getStringParam(search, 'user', { fallback: null });
    if (user) {
      setSelectedUser(user);
      // Mark the selection as settled so the savedUserRef restore effect does
      // not overwrite the URL-seeded user. The unqualified-user swap effect
      // is also gated on this flag, which is acceptable: a shared link to an
      // unqualified forecaster shows that forecaster's (empty) view honestly.
      hasAutoSelectedRef.current = true;
      // A link can still point at a forecaster who doesn't participate in the
      // resource in view, so the selection is validated once the list lands.
      pendingSelectionCheckRef.current = true;
    }

    const month = getIntParam(search, 'month', { fallback: null, min: 1, max: 12 });
    if (month) setSharedMonth(month);

    const year = getIntParam(search, 'year', { fallback: null, min: 2000, max: 2100 });
    if (year) setSharedYear(year);

    const horizon = getIntParam(search, 'horizon', { fallback: null, min: 0, max: 14 });
    if (horizon !== null) dispatch(setSelectedReportHorizon(horizon));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State -> URL (replace, no history spam) so the current view is shareable.
  // Gated on urlSeeded so default state never overwrites params before the
  // seed effect has applied them.
  useEffect(() => {
    if (!urlSeeded.current) return;
    const next = mergeSearch(window.location.search, {
      tab: selectedReport ? (TAB_TO_URL[selectedReport] ?? selectedReport) : null,
      track: forecastType || null,
      // Forecasters can only ever see themselves; putting their own id in the
      // URL is noise (and the seed ignores it anyway), so the param is
      // reserved for viewers who can select other users.
      user: !hasNoPermission && selectedUser ? selectedUser : null,
      month: sharedMonth || null,
      year: sharedYear || null,
      horizon: selectedReportHorizon ?? null,
    });
    if (next !== window.location.search) {
      navigate({ search: next }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, forecastType, selectedUser, sharedMonth, sharedYear, selectedReportHorizon]);

  // Auto-select current user if they don't have permission to view others (forecaster)
  useEffect(() => {
    if (hasNoPermission && user?.id && !selectedUser && !hasAutoSelectedRef.current) {
      setSelectedUser(user.id);
      hasAutoSelectedRef.current = true;
    }
  }, [hasNoPermission, user?.id, selectedUser]);

  // Seed the forecaster's Overview spotlight to their own row, mirroring the
  // old default. Runs once; clearing it afterwards is respected (see ref).
  useEffect(() => {
    if (!isForecaster || !user?.id || hasSeededForecasterTraceRef.current) return;
    setForecasterTrace(user.id);
    hasSeededForecasterTraceRef.current = true;
  }, [isForecaster, user?.id]);

  // Auto-select first participant *with metrics* once the filtered list lands.
  // We need filteredParticipants in deps because participants/activeUsers are
  // fetched async — on first mount the list is empty, and without re-running
  // when it loads, no user is ever selected and the page stays blank.
  // hasAutoSelectedRef + savedUserRef guards prevent us from clobbering a
  // restored or user-changed selection. We wait for activeUsers to land
  // before picking so `has_stats` is meaningful — otherwise the fallback
  // branch in `filteredParticipants` returns the raw participants list
  // (no has_stats tag), and we'd pick `participants[0]` who might be
  // unqualified, leaving the analytics empty.
  useEffect(() => {
    if (hasNoPermission || hasAutoSelectedRef.current || selectedUser || savedUserRef) return;
    // Trends defaults to "no one selected" (the matrix shows the whole field);
    // the other tabs render a single forecaster and default to the first below.
    if (selectedReport === 'trends') return;
    if (filteredParticipants.length === 0) return;
    if (activeUsersLoading) return;
    if (!Array.isArray(activeUsers) || activeUsers.length === 0) return;

    const firstWithStats = filteredParticipants.find((p) => p.has_stats);
    const pick = firstWithStats || filteredParticipants[0];
    setSelectedUser(pick.user);
    hasAutoSelectedRef.current = true;
  }, [hasNoPermission, selectedUser, filteredParticipants, activeUsers, activeUsersLoading, selectedReport]);

  // The non-Trends tabs render a single forecaster, so they need a selection.
  // When one is active with none (e.g. the user cleared it on Trends, where an
  // empty selection is valid), fall back to the first participant with metrics.
  // Unlike the initial auto-select above, this is intentionally NOT gated by
  // hasAutoSelectedRef, so it re-defaults after a deliberate clear.
  useEffect(() => {
    if (hasNoPermission || selectedReport === 'trends' || selectedUser) return;
    if (filteredParticipants.length === 0 || activeUsersLoading) return;
    if (!Array.isArray(activeUsers) || activeUsers.length === 0) return;
    const firstWithStats = filteredParticipants.find((p) => p.has_stats);
    setSelectedUser((firstWithStats || filteredParticipants[0]).user);
  }, [hasNoPermission, selectedReport, selectedUser, filteredParticipants, activeUsers, activeUsersLoading]);

  // Sync forecast type selection with Redux metric
  useEffect(() => {
    dispatch(setSelectedMetricAction(currentMetric));
  }, [currentMetric, dispatch]);

  // Save state to module-level variables whenever they change (persists across navigation)
  useEffect(() => {
    // For admins/market owners: save selected user
    // For forecasters: don't save user (they can only see themselves)
    if (!hasNoPermission && selectedUser) {
      savedUserRef = selectedUser;
    }
  }, [selectedUser, hasNoPermission]);

  useEffect(() => {
    // Save shared month/year for Rankings and Errors sections
    savedYearRef = sharedYear;
  }, [sharedYear]);

  useEffect(() => {
    savedMonthRef = sharedMonth;
  }, [sharedMonth]);

  useEffect(() => {
    savedForecastTypeRef = forecastType;
  }, [forecastType]);

  // Reset the selected horizon only when the RESOURCE genuinely changes — never
  // on mount, and never on a mere period (month/year) change. On entry we keep
  // the horizon persisted in the store (redux survives in-app navigation), so
  // the horizon-scoped fetches (monthly stats, gate closure, month-over-month)
  // fire immediately and in parallel with the horizon-overview fetch — instead
  // of waiting for the overview→auto-pick round-trip first (the old
  // reset-on-mount waterfall). The auto-select effect below re-picks (or
  // corrects) the horizon once the NEW period's overview lands.
  //
  // We deliberately do NOT reset on a period change: nulling the horizon there
  // re-runs the auto-select against the PREVIOUS period's (stale) overview,
  // which would snap the filter to Intraday while the prior horizon's results
  // are still shown. Keeping the horizon lets the auto-select validate it
  // against the new period's overview and only fall back to the lowest if it is
  // genuinely unavailable. Different resources have different horizon sets, so
  // a resource switch still warrants a clean reset.
  const prevResourceRef = React.useRef(null);
  useEffect(() => {
    if (selectedResource?.id == null) return; // resource not resolved yet
    const prev = prevResourceRef.current;
    prevResourceRef.current = selectedResource.id;
    // First real resource (mount) or no actual change → keep persisted horizon.
    if (prev === null || prev === selectedResource.id) return;
    dispatch(setSelectedReportHorizon(null));
  }, [dispatch, selectedResource?.id]);

  // Restore saved user selection on mount (only for admins/market owners).
  // Only restore once activeUsers has loaded so we can validate the saved
  // user is still qualified for the current period — if they aren't, skip
  // the restore so the auto-pick effect above takes over with a qualified
  // user (otherwise the analytics views render an empty state for an
  // unqualified user the admin didn't actively choose this period).
  useEffect(() => {
    if (hasNoPermission || !savedUserRef || hasAutoSelectedRef.current) return;
    if (selectedUser === savedUserRef) return;
    if (activeUsersLoading) return;
    if (!Array.isArray(activeUsers) || activeUsers.length === 0) return;

    const savedRow = activeUsers.find((u) => u.user_id === savedUserRef);
    if (!savedRow || savedRow.has_stats !== true) return;

    setSelectedUser(savedUserRef);
    hasAutoSelectedRef.current = true;
  }, [hasNoPermission, selectedUser, activeUsers, activeUsersLoading]);

  // If the initial selectedUser (sourced from savedUserRef via useState init)
  // turns out to be unqualified for the current period once activeUsers has
  // loaded, swap to a qualified participant. Only acts before any auto- or
  // restore-pick has run (`hasAutoSelectedRef === false`) so user-driven
  // dropdown choices made later are never overridden.
  useEffect(() => {
    if (hasNoPermission || hasAutoSelectedRef.current) return;
    if (!selectedUser) return;
    if (activeUsersLoading) return;
    if (!Array.isArray(activeUsers) || activeUsers.length === 0) return;
    if (filteredParticipants.length === 0) return;

    const currentRow = activeUsers.find((u) => u.user_id === selectedUser);
    if (currentRow && currentRow.has_stats === true) return;

    const firstWithStats = filteredParticipants.find((p) => p.has_stats);
    if (!firstWithStats || firstWithStats.user === selectedUser) return;

    setSelectedUser(firstWithStats.user);
    hasAutoSelectedRef.current = true;
  }, [hasNoPermission, selectedUser, activeUsers, activeUsersLoading, filteredParticipants]);

  // Fetch active users for participant filtering (for rankings, errors, and payments tabs)
  useEffect(() => {
    // Only fetch if we're on rankings, errors, or payments tab
    if (selectedReport !== 'rankings' && selectedReport !== 'errors' && selectedReport !== 'payments') {
      return;
    }

    // Don't fetch active users if user doesn't have permission (forecaster)
    // They will only see their own data, so no need to fetch the list
    if (hasNoPermission) {
      return;
    }

    // Determine which year/month to use based on current section
    let year = sharedYear;
    let month = sharedMonth;
    if (selectedReport === 'payments') {
      year = paymentsYear;
      month = paymentsMonth;
    }

    // Ensure all required params are present
    const requiredParamsPresent = selectedResource?.id && year && month && forecastType;

    if (!requiredParamsPresent) {
      return;
    }

    // Fetch active users for admins/market owners to populate the user selector
    const activeUsersParams = {
      resourceId: selectedResource.id,
      year: year,
      month: month,
      track: forecastType,
    };
    dispatch(fetchActiveUsers(activeUsersParams));
  }, [dispatch, selectedResource?.id, sharedYear, sharedMonth, paymentsYear, paymentsMonth, forecastType, selectedReport, hasNoPermission]);

  // Fetch monthly stats for selected user (only for rankings and errors tabs;
  // on phones the summary view needs this payload whatever the hidden active
  // tab is, so the gate is relaxed there)
  useEffect(() => {
    if (selectedReport !== 'rankings' && selectedReport !== 'errors' && !IS_PHONE_VIEWPORT) {
      return;
    }

    // Determine which year/month to use based on current section
    let year = sharedYear;
    let month = sharedMonth;

    // Ensure all required params are present
    const requiredParamsPresent = selectedResource?.id && year && month && forecastType;

    // For monthly stats, only fetch if user is selected
    if (!requiredParamsPresent || !selectedUser) {
      return;
    }

    const fetchParams = {
      userId: selectedUser,
      resourceId: selectedResource.id,
      year: year,
      month: month,
      track: forecastType,
      horizonOffset: selectedReportHorizon,
    };

    dispatch(fetchMonthlyStats(fetchParams));
  }, [dispatch, selectedResource?.id, selectedUser, sharedYear, sharedMonth, forecastType, selectedReport, selectedReportHorizon]);

  // Fetch per-horizon overview for rankings and errors tabs
  useEffect(() => {
    if (selectedReport !== 'rankings' && selectedReport !== 'errors') {
      return;
    }

    const requiredParamsPresent = selectedResource?.id && sharedYear && sharedMonth && forecastType;
    if (!requiredParamsPresent || !selectedUser) {
      return;
    }

    const fetchParams = {
      userId: selectedUser,
      resourceId: selectedResource.id,
      year: sharedYear,
      month: sharedMonth,
      track: forecastType,
    };

    dispatch(fetchHorizonOverview(fetchParams));
  }, [dispatch, selectedReport, selectedResource?.id, selectedUser, sharedYear, sharedMonth, forecastType]);

  // Auto-select the first available horizon as the default detail view.
  // The "overview" landing page was removed, so we land users directly on
  // the lowest horizon (typically Intraday) once the horizon list loads.
  useEffect(() => {
    // Only Rankings/Errors fetch (and are scoped by) the per-user horizon
    // overview. On other tabs the redux rows are stale leftovers, and
    // validating against them would snap back any horizon the user picks
    // there (e.g. Intraday on the Overview tab, whose pills are not limited
    // to the forecaster's data horizons).
    if (selectedReport !== 'rankings' && selectedReport !== 'errors') return;
    if (!Array.isArray(horizonOverviewData) || horizonOverviewData.length === 0) return;

    const sorted = [...horizonOverviewData].sort((a, b) => (a.horizon_offset || 0) - (b.horizon_offset || 0));
    // Keep a persisted horizon if it's still valid for this period; otherwise
    // (null on cold load, or stale after a period change) land on the lowest.
    const isValid = selectedReportHorizon !== null
      && sorted.some((h) => h.horizon_offset === selectedReportHorizon);
    if (isValid) return;

    dispatch(setSelectedReportHorizon(sorted[0].horizon_offset));
  }, [dispatch, selectedReport, horizonOverviewData, selectedReportHorizon]);

  // The Trends tab doesn't fetch the per-user horizon overview that drives the
  // auto-select above, so seed a sensible default horizon from the canonical
  // resource list when none is selected yet.
  //
  // Also CORRECT a horizon that isn't valid for the current resource. On a
  // resource switch the horizon is reset to null (effect above), but
  // `availableHorizons` (redux) still holds the previous resource's list until
  // the new fetch lands, so the null-seed can pick a stale horizon (e.g.
  // Intraday) that the new resource doesn't offer. Without this correction the
  // header/chart stay pinned to that stale horizon while the filter pane shows
  // the new resource's horizons. Once the canonical list resolves we snap to
  // its lowest horizon. A user-picked horizon is always a member of the list,
  // so this never fights deliberate selections.
  useEffect(() => {
    if (selectedReport !== 'trends') return;
    if (!Array.isArray(availableHorizons) || availableHorizons.length === 0) return;
    if (selectedReportHorizon != null && availableHorizons.includes(selectedReportHorizon)) return;
    dispatch(setSelectedReportHorizon(availableHorizons[0]));
  }, [dispatch, selectedReport, selectedReportHorizon, availableHorizons]);

  // Fetch month-over-month comparison stats (only for rankings and errors tabs)
  useEffect(() => {
    // Only fetch if we're on rankings or errors tab
    if (selectedReport !== 'rankings' && selectedReport !== 'errors') {
      return;
    }

    // Determine which year/month to use based on current section
    let year = sharedYear;
    let month = sharedMonth;

    // Ensure all required params are present
    const requiredParamsPresent = selectedResource?.id && year && month && forecastType;

    // For month-over-month stats, only fetch once a horizon is selected so the
    // comparison is scoped to it. Without a horizon the backend would compare
    // arbitrary horizon rows (e.g. Intraday this month vs D+1 last month).
    if (!requiredParamsPresent || !selectedUser || selectedReportHorizon == null) {
      return;
    }

    const fetchParams = {
      userId: selectedUser,
      resourceId: selectedResource.id,
      year: year,
      month: month,
      track: forecastType,
      horizonOffset: selectedReportHorizon,
    };

    dispatch(fetchMonthOverMonthStats(fetchParams));
  }, [dispatch, selectedResource?.id, selectedUser, sharedYear, sharedMonth, forecastType, selectedReport, selectedReportHorizon]);

  // Fetch per-gate-closure data whenever a detail view is active (any
  // horizon, not just intraday). The Errors tab uses this list to populate
  // the gate-closure filter for D+1 / D+N. Intraday (horizon=0) currently
  // has no per-gate rows emitted by the backend, so the response is empty
  // there and the downstream UI degrades gracefully.
  useEffect(() => {
    if (selectedReportHorizon == null) return;
    if (selectedReport !== 'rankings' && selectedReport !== 'errors') return;
    if (!selectedResource?.id || !selectedUser) return;

    dispatch(fetchGateClosureOverview({
      userId: selectedUser,
      resourceId: selectedResource.id,
      year: sharedYear,
      month: sharedMonth,
      track: forecastType,
      horizonOffset: selectedReportHorizon,
    }));
  }, [dispatch, selectedReportHorizon, selectedReport, selectedResource?.id, selectedUser, sharedYear, sharedMonth, forecastType]);

  // Fetch detailed stats when a specific gate hour is selected (drill-down).
  // Works for any detail horizon — the backend per-gate row carries the
  // residual distribution + power-interval boxplot JSON for that gate.
  useEffect(() => {
    if (selectedGateHour == null || selectedReportHorizon == null) return;
    if (!selectedResource?.id || !selectedUser) return;

    dispatch(fetchGateHourDetail({
      userId: selectedUser,
      resourceId: selectedResource.id,
      year: sharedYear,
      month: sharedMonth,
      track: forecastType,
      horizonOffset: selectedReportHorizon,
      gateClosureHour: selectedGateHour,
    }));
  }, [dispatch, selectedGateHour, selectedReportHorizon, selectedResource?.id, selectedUser, sharedYear, sharedMonth, forecastType]);

  // Handler for pill bar: switches the detail view to the chosen horizon.
  const handlePillHorizonSelect = (horizon) => {
    dispatch(clearGateHourDetail());
    dispatch(setSelectedReportHorizon(horizon));
  };

  // Handler for selecting a gate hour in the intraday detail view
  const handleGateHourSelect = (gateHour) => {
    dispatch(setSelectedGateHour(gateHour));
  };

  // Open per-day drill-down drawer. Deterministic-only for v1: the slot
  // matrix is persisted for q50 only, so probabilistic clicks are no-ops.
  const handleDayClick = (targetDay, horizonOffsetOverride) => {
    if (!targetDay || !selectedResource?.id || !selectedUser) return;
    if (forecastType !== 'deterministic') return;
    const horizon = typeof horizonOffsetOverride === 'number'
      ? horizonOffsetOverride
      : (typeof selectedReportHorizon === 'number' ? selectedReportHorizon : 0);
    dispatch(openDayDetail({
      targetDay,
      userId: selectedUser,
      resourceId: selectedResource.id,
      horizonOffset: horizon,
      metric: 'rmse',
      track: forecastType,
    }));
  };

  // Strip / inlined-grid tiles get a "click disabled — try deterministic"
  // tooltip when the user is on the probabilistic track, so the no-op isn't
  // invisible.
  const dayClickDisabledHint = forecastType !== 'deterministic'
    ? 'Drill-down available on deterministic track only'
    : undefined;

  const reports = [
    { id: 'trends', title: 'Overview' },
    { id: 'rankings', title: 'Ranking' },
    { id: 'errors', title: 'Errors' },
    { id: 'payments', title: 'Payments' },
    // { id: 'insights', title: 'Insights' }, // Temporarily disabled
  ];

  // Left menu click handler
  const onSelectReport = (id) => {
    // When switching to Payments section, default to previous month
    if (id === 'payments') {
      savedMonthRef = previousMonthYear.month;
      setPaymentsYear(previousMonthYear.year);
      setPaymentsMonth(previousMonthYear.month);
    }
    setSelectedReport(id);
  };

  // Re-pivot the whole analytics view to a forecaster clicked in any peer
  // leaderboard. Admins / market owners only (forecasters can't view others,
  // so the handler is never passed down for them). We deliberately do NOT
  // move the viewport — a selection change shouldn't yank the page. Feedback
  // is in-context: the progress bar + skeleton signal the reload, and the
  // rail re-centers on the newly selected row and pulses it briefly.
  const handleSelectForecaster = useCallback(
    (userId) => {
      if (hasNoPermission) return;
      // A null/empty userId clears the selection (used by the Trends matrix to
      // toggle a forecaster off when their square is clicked again).
      setSelectedUser(userId || '');
    },
    [hasNoPermission],
  );

  // Drive the top progress bar: any in-flight analytics fetch counts. This
  // covers the Dashboard→Analytics waterfall (participants → active users →
  // monthly stats) and the reload that follows a forecaster switch.
  const analyticsLoading =
    participantsLoading ||
    activeUsersLoading ||
    monthlyStatsLoading ||
    horizonOverviewLoading;

  return (
    // Shares the market's excluded days with the notice and every chart.
    <ExcludedDaysProvider resourceId={selectedResource?.id}>
      {/* Rendered OUTSIDE the space-y-4 container on purpose: it's a fixed,
          out-of-flow element, but as a `space-y-4` child its mount/unmount
          would toggle the `> * + *` top margin on the next sibling (the
          tabs), making the whole section jump ~16px whenever loading starts
          or stops. Keeping it a sibling of the container avoids that. */}
      <TopProgressBar active={analyticsLoading} />
    <div className="space-y-4">
      {/* Phone summary for forecasters: one glanceable scroll instead of the
          tabbed UI. Admin views keep the tabs at every size. */}
      {mobileSummaryActive && (
        <div className="lg:hidden space-y-4">
          <div className="flex justify-end">
            <AnalyticsFiltersBar
              inline
              showTrack
              forecastType={forecastType}
              onForecastTypeChange={setForecastType}
              showForecaster={false}
              blurSensitive={blurSensitive}
              showHorizon
              selectedHorizon={selectedReportHorizon}
              onHorizonChange={handlePillHorizonSelect}
              availableHorizons={availableHorizons}
              horizonsWithData={horizonsWithData}
              selectedYear={sharedYear}
              onYearChange={setSharedYear}
              selectedMonthNum={sharedMonth}
              onMonthChange={setSharedMonth}
              monthOptions={monthOptions}
              yearOptions={yearOptions}
            />
          </div>
          {/* The summary follows the shared month, like its filters bar. */}
          <ExcludedDaysNotice year={sharedYear} month={sharedMonth}/>
          <MobileAnalyticsSummary
            selfUserId={user?.id || null}
            year={sharedYear}
            month={sharedMonth}
            metricLabel={currentMetricLabel}
            blurSensitive={blurSensitive}
          >
            <PaymentsReport
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              selectedYear={paymentsYear}
              setSelectedYear={setPaymentsYear}
              selectedMonthNum={paymentsMonth}
              setSelectedMonthNum={setPaymentsMonth}
              participants={[]}
              participantsLoading={false}
              monthOptions={monthOptions}
              yearOptions={yearOptions}
              hideUserSelector
              resourceId={selectedResource?.id}
              blurSensitive={blurSensitive}
            />
          </MobileAnalyticsSummary>
        </div>
      )}

      {/* Tabs + inline filters: stack on mobile, side-by-side from md up */}
      <div className={`border-b border-gray-200/70 dark:border-gray-600/70 flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 ${mobileSummaryActive ? 'hidden lg:flex' : 'flex'}`}>
        <nav className="flex gap-8" aria-label="Tabs">
          {reports.map((report) => (
            <button
              key={report.id}
              onClick={() => onSelectReport(report.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                selectedReport === report.id
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {report.title}
            </button>
          ))}
        </nav>

        {/* Inline filter bar — props differ per active tab. Rankings, Errors
            and Trends share the same bar. */}
        {selectedReport === 'rankings' || selectedReport === 'errors' || selectedReport === 'trends' ? (
          <AnalyticsFiltersBar
            inline
            showTrack
            forecastType={forecastType}
            onForecastTypeChange={setForecastType}
            showForecaster={!hasNoPermission}
            selectedUser={selectedUser}
            onUserChange={setSelectedUser}
            participants={filteredParticipants}
            participantsLoading={participantsLoading || activeUsersLoading}
            blurSensitive={blurSensitive}
            showHorizon
            selectedHorizon={selectedReportHorizon}
            onHorizonChange={handlePillHorizonSelect}
            availableHorizons={availableHorizons}
            horizonsWithData={selectedReport === 'trends' ? [] : horizonsWithData}
            selectedYear={sharedYear}
            onYearChange={setSharedYear}
            selectedMonthNum={sharedMonth}
            onMonthChange={setSharedMonth}
            monthOptions={monthOptions}
            yearOptions={yearOptions}
            showEnsembleModel={selectedReport === 'trends' && !isForecaster}
            ensembleModel={trendsEnsembleModel}
            onEnsembleModelChange={setTrendsEnsembleModel}
            ensembleModelOptions={ENSEMBLE_MODELS}
            showOracle={selectedReport === 'trends' && !isForecaster}
            oracleEnabled={showOracle}
            onOracleChange={setShowOracle}
            showReference={selectedReport === 'trends' && !isForecaster}
            referenceEnabled={showReference}
            onReferenceChange={setShowReference}
          />
        ) : selectedReport === 'payments' ? (
          <AnalyticsFiltersBar
            inline
            showTrack={false}
            showForecaster={!hasNoPermission}
            selectedUser={selectedUser}
            onUserChange={setSelectedUser}
            participants={filteredParticipants}
            participantsLoading={participantsLoading || activeUsersLoading}
            blurSensitive={blurSensitive}
            selectedYear={paymentsYear}
            onYearChange={setPaymentsYear}
            selectedMonthNum={paymentsMonth}
            onMonthChange={setPaymentsMonth}
            monthOptions={monthOptions}
            yearOptions={yearOptions}
          />
        ) : null}
      </div>

      {/* Full-width content area */}
      <main className={mobileSummaryActive ? 'hidden lg:block' : undefined}>
        <ExcludedDaysNotice
          year={selectedReport === 'payments' ? paymentsYear : sharedYear}
          month={selectedReport === 'payments' ? paymentsMonth : sharedMonth}
        />
        {/* `key` replays the fade-up each time the active section changes */}
        <div key={selectedReport} className="view-enter">
          {/*
            When monthlyStats returned no row for the selected user/period,
            we have nothing to render in the rankings/errors views — typically
            because the forecaster's D+N submissions failed Rule 7's strict
            per-challenge compliance and aggregation produced only penalty
            rows. Surface a compliance empty-state with a per-session heatmap
            so the forecaster can see exactly which gate closures they missed.
          */}
          {(selectedReport === 'rankings' || selectedReport === 'errors') &&
            !monthlyStatsLoading &&
            !monthlyStatsError &&
            selectedUser &&
            !(monthlyStatsData?.daily_ranks_json?.length > 0) ? (
              <DNComplianceEmptyState
                resourceId={selectedResource?.id}
                year={sharedYear}
                month={sharedMonth}
                userId={selectedUser}
                initialHorizon={selectedReportHorizon}
              />
          ) : selectedReport === 'rankings' ? (
              <RankingsReport
                forecastType={forecastType}
                setForecastType={setForecastType}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                selectedYear={sharedYear}
                setSelectedYear={setSharedYear}
                selectedMonthNum={sharedMonth}
                setSelectedMonthNum={setSharedMonth}
                participants={filteredParticipants}
                participantsLoading={participantsLoading || activeUsersLoading}
                monthOptions={monthOptions}
                yearOptions={yearOptions}
                monthlyStatsData={monthlyStatsData}
                monthlyStatsLoading={monthlyStatsLoading}
                monthlyStatsError={monthlyStatsError}
                monthOverMonthData={monthOverMonthData}
                monthOverMonthLoading={monthOverMonthLoading}
                monthOverMonthError={monthOverMonthError}
                horizonOverviewData={horizonOverviewData}
                horizonOverviewLoading={horizonOverviewLoading}
                horizonOverviewError={horizonOverviewError}
                selectedHorizon={selectedReportHorizon}
                onSelectHorizon={handlePillHorizonSelect}
                availableHorizons={availableHorizons}
                gateClosureData={gateClosureData}
                gateClosureLoading={gateClosureLoading}
                selectedGateHour={selectedGateHour}
                onSelectGateHour={handleGateHourSelect}
                gateHourDetailData={gateHourDetailData}
                gateHourDetailLoading={gateHourDetailLoading}
                hideUserSelector={hasNoPermission}
                blurSensitive={blurSensitive}
                onDayClick={handleDayClick}
                dayClickDisabledHint={dayClickDisabledHint}
                onSelectForecaster={hasNoPermission ? undefined : handleSelectForecaster}
              />
            ) : selectedReport === 'errors' ? (
              <ErrorsReport
                forecastType={forecastType}
                setForecastType={setForecastType}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                selectedYear={sharedYear}
                setSelectedYear={setSharedYear}
                selectedMonthNum={sharedMonth}
                setSelectedMonthNum={setSharedMonth}
                participants={filteredParticipants}
                participantsLoading={participantsLoading || activeUsersLoading}
                monthOptions={monthOptions}
                yearOptions={yearOptions}
                currentMetricLabel={currentMetricLabel}
                monthlyStatsData={monthlyStatsData}
                monthlyStatsLoading={monthlyStatsLoading}
                monthlyStatsError={monthlyStatsError}
                monthOverMonthData={monthOverMonthData}
                monthOverMonthLoading={monthOverMonthLoading}
                monthOverMonthError={monthOverMonthError}
                horizonOverviewData={horizonOverviewData}
                horizonOverviewLoading={horizonOverviewLoading}
                horizonOverviewError={horizonOverviewError}
                selectedHorizon={selectedReportHorizon}
                onSelectHorizon={handlePillHorizonSelect}
                availableHorizons={availableHorizons}
                gateClosureData={gateClosureData}
                gateClosureLoading={gateClosureLoading}
                selectedGateHour={selectedGateHour}
                onSelectGateHour={handleGateHourSelect}
                gateHourDetailData={gateHourDetailData}
                gateHourDetailLoading={gateHourDetailLoading}
                hideUserSelector={hasNoPermission}
                canViewEmails={!hasNoPermission}
                blurSensitive={blurSensitive}
                onDayClick={handleDayClick}
                dayClickDisabledHint={dayClickDisabledHint}
                onSelectForecaster={hasNoPermission ? undefined : handleSelectForecaster}
                marketName={resourceDisplayName(selectedResource)}
              />
            ) : selectedReport === 'payments' ? (
              <PaymentsReport
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                selectedYear={paymentsYear}
                setSelectedYear={setPaymentsYear}
                selectedMonthNum={paymentsMonth}
                setSelectedMonthNum={setPaymentsMonth}
                participants={filteredParticipants}
                participantsLoading={participantsLoading || activeUsersLoading}
                monthOptions={monthOptions}
                yearOptions={yearOptions}
                hideUserSelector={hasNoPermission}
                resourceId={selectedResource?.id}
                blurSensitive={blurSensitive}
              />
            ) : selectedReport === 'trends' ? (
              <TrendsReport
                resourceId={selectedResource?.id}
                track={forecastType}
                grain="day"
                horizon={selectedReportHorizon}
                traceUserId={isForecaster ? forecasterTrace : selectedUser}
                onTrace={isForecaster ? handleForecasterTrace : (hasNoPermission ? null : handleSelectForecaster)}
                isForecaster={isForecaster}
                viewerUserId={user?.id}
                year={sharedYear}
                month={sharedMonth}
                blurSensitive={blurSensitive}
                ensembleModel={isForecaster ? null : trendsEnsembleModel}
                showOracle={!isForecaster && showOracle}
                referenceEnabled={!isForecaster && showReference}
              />
            ) : selectedReport === 'insights' ? (
              <InsightsReport />
            ) : null}
        </div>
      </main>
      <DayDetailDrawer />
    </div>
    </ExcludedDaysProvider>
  );
}
